<template>
  <section style="padding: 50px; margin-top: 60px;">
    <div class="hero-section">
      <!-- ░░ World map background (plus visible, style Figma) ░░ -->
      <div class="hero-map" aria-hidden="true">
        <img src="@/assets/img/798ced8ea85131aeac3e881ba7cf3dfd9d9c0a7a.png" alt="" class="hero-map__img" />
      </div>

      <!-- ░░ Subtle radial glow for depth ░░ -->
      <div class="hero-glow" aria-hidden="true"></div>

      <!-- ░░ Badge -24h (PNG image) ░░ -->
      <div class="hero-badge" aria-label="Traitement en moins de 24h">
        <img src="@/assets/img/illu.png" alt="-24h" class="hero-badge__img" />
      </div>

      <!-- ░░ Globe 3D currencies ░░ -->
      <div class="hero-globe" aria-hidden="true">
        <img src="@/assets/img/b96777aa923b9573f6143eaaf0beaf7f48611e96.png" alt="" class="hero-globe__img" />
      </div>

      <!-- ░░ Central content ░░ -->
      <div class="hero-content">
        <h1 class="hero-title">
          Paiements<br />
          internationaux pour<br />
          entreprises africaines
        </h1>

        <p class="hero-subtitle">
          Payez vos fournisseurs et encaissez vos clients étrangers<br class="br-desktop" />
          facilement, depuis le Sénégal.
        </p>

        <custom-button label="Demander une cotation" @click="handleCotation" />
      </div>
    </div>
  </section>
</template>

<script>
import CustomButton from '@/components/CustomButton.vue'

export default {
  name: 'HeroSection',

  components: {
    CustomButton,
  },

  methods: {
    handleCotation() {
      this.$router.push('/devis')
    },
  },
}
</script>

<style scoped>
/* ─── Design tokens ─────────────────────────────────── */
.hero-section {
  --bg:           #2F2113;                    /* ← Couleur Figma exacte, opaque */
  --glow-color:   rgba(100, 65, 25, 0.35);    /* ← Glow plus subtil */
  --text-primary: #ffffff;
  --text-muted:   rgba(255, 255, 255, 0.75);  /* ← Sous-titre plus lisible */
  --radius:       26px;
}

/* ─── Section shell ─────────────────────────────────── */
.hero-section {
  position: relative;
  width: 100%;
  background-color: var(--bg);
  border-radius: var(--radius);
  overflow: hidden;
  padding: 64px 48px 68px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
  box-shadow: none;
}

/* ─── World-map dots (style Figma - plus visible) ───── */
.hero-map {
  position: absolute;
  inset: 0;                                    /* ← Prend toute la place */
  z-index: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-map__img {
  width: 110%;                                 /* ← Légèrement plus grand */
  height: 110%;
  object-fit: cover;                           /* ← Couvre tout le fond */
  object-position: center;
  opacity: 0.55;                               /* ← Beaucoup plus visible */
  filter: 
    brightness(1.4)                            /* ← Plus clair/blanc */
    contrast(1.2)                              /* ← Plus de contraste */
    saturate(0);                               /* ← Noir et blanc/neige */
  mix-blend-mode: screen;                      /* ← Fusionne avec le fond marron */
}

/* ─── Subtle radial glow (optionnel, pour la profondeur) ─ */
.hero-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 80% 70% at 50% 30%,
    var(--glow-color) 0%,
    transparent 60%
  );
  pointer-events: none;
  z-index: 1;
}

/* ─── Badge -24h ─────────────────────────────────────── */
.hero-badge {
  position: absolute;
  left: 52px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: clamp(80px, 8vw, 110px);
  animation: floatBadge 4s ease-in-out infinite;
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.50));
}

.hero-badge__img {
  width: 100%;
  height: auto;
  display: block;
  mix-blend-mode: lighten;
  opacity: 0.95;
}

/* ─── Globe ──────────────────────────────────────────── */
.hero-globe {
  position: absolute;
  right: 44px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: clamp(160px, 18vw, 260px);            /* ← Légèrement plus grand */
  animation: floatGlobe 5s ease-in-out infinite;
}

.hero-globe__img {
  width: 100%;
  height: auto;
  display: block;
  mix-blend-mode: normal;                      /* ← Pas de blend, couleurs natives */
  opacity: 1;                                  /* ← Pleine opacité */
  filter: drop-shadow(0 10px 30px rgba(0,0,0,0.3));
}

/* ─── Central content ────────────────────────────────── */
.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 clamp(120px, 16vw, 220px);        /* ← Plus d'espace latéral */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;                                   /* ← Plus d'espace entre éléments */
  width: 100%;
  box-sizing: border-box;
}

/* ─── Title ──────────────────────────────────────────── */
.hero-title {
  margin: 0;
  color: var(--text-primary);
  font-family: 'Sora', 'Nunito', 'DM Sans', system-ui, sans-serif;
  font-size: clamp(2.2rem, 5.2vw, 4.2rem);     /* ← Légèrement plus grand */
  font-weight: 800;
  line-height: 1.08;                           /* ← Plus serré comme Figma */
  letter-spacing: -0.02em;
}

/* ─── Subtitle ───────────────────────────────────────── */
.hero-subtitle {
  margin: 0;
  color: var(--text-muted);
  font-family: 'Sora', 'DM Sans', system-ui, sans-serif;
  font-size: clamp(0.95rem, 1.4vw, 1.15rem);   /* ← Plus grand */
  font-weight: 400;
  line-height: 1.6;
  max-width: 600px;                            /* ← Limite la largeur */
}

/* ─── Float animations ───────────────────────────────── */
@keyframes floatBadge {
  0%, 100% { transform: translateY(-50%); }
  50%       { transform: translateY(calc(-50% - 9px)); }
}

@keyframes floatGlobe {
  0%, 100% { transform: translateY(-50%); }
  50%       { transform: translateY(calc(-50% - 11px)); }
}

/* ─── Tablet (≤ 900px) ───────────────────────────────── */
@media (max-width: 900px) {
  .hero-section {
    padding: 56px 32px 64px;
    min-height: 420px;
  }

  .hero-content {
    padding: 0 clamp(100px, 12vw, 160px);
  }

  .hero-badge {
    left: 28px;
  }

  .hero-globe {
    right: 20px;
    width: clamp(140px, 16vw, 200px);
  }
  
  .hero-map__img {
    opacity: 0.45;                             /* ← Moins visible sur mobile */
  }
}

/* ─── Mobile (≤ 640px) ───────────────────────────────── */
@media (max-width: 640px) {
  .hero-section {
    padding: 110px 20px 56px;
    border-radius: 18px;
    min-height: unset;
    align-items: flex-start;
  }

  .hero-badge {
    top: 20px;
    left: 20px;
    transform: none;
    width: 72px;
    animation: floatSimple 4s ease-in-out infinite;
  }

  .hero-globe {
    top: 10px;
    right: 16px;
    transform: none;
    width: 130px;
    animation: floatSimple 5s ease-in-out 0.5s infinite;
  }

  .hero-content {
    padding: 0;
    gap: 18px;
    align-items: center;
    width: 100%;
  }
  
  .hero-map__img {
    opacity: 0.35;                             /* ← Encore moins visible */
    width: 150%;
    height: 150%;
  }

  .br-desktop {
    display: none;
  }
}

@keyframes floatSimple {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-7px); }
}
</style>